import React, { useEffect, useRef, useState } from 'react';
import { collabApi, avatarStyle } from '../utils/collab';

/**
 * The circles in the header: who else from this account is in the app right now.
 *
 * The blinking dot is `unread > 0`, and `unread` is whatever the server last said —
 * `read_at IS NULL` counted in SQL (Critical Note #72). It is never derived from
 * anything this component keeps, so it survives a reload and a missed poll.
 *
 * The server also sends offline people who have an existing thread with me. Only those
 * with an UNREAD message get a circle; the rest live in the overflow list ("…" chip), so
 * the rail stays a picture of who is here.
 *
 * But being here is not what makes somebody reachable: a message can be sent to ANY user
 * of the account, and they read it when they next sign in. So the overflow chip is always
 * present, and its panel ends with the rest of the account directory — fetched when the
 * panel opens, never held by the page, since `App` re-renders every second (Note #59).
 */

const MAX_VISIBLE = 3;

function Avatar({ person, size, onClick, stacked = false, bare = false }) {
  const style = {
    ...avatarStyle(person.avatar_color),
    ...(size ? { width: size, height: size } : null),
    marginRight: stacked ? -8 : 0,
  };
  const unread = person.unread || 0;
  const title = `${person.display_name}${person.where ? ` — viewing ${person.where}` : ''}`
    + (unread ? ` · ${unread} unread` : '');

  return (
    <button
      type="button"
      className={bare
        ? `collab-av${person.online ? ' collab-av--online' : ''}`
        : `collab-avatar${person.online ? ' collab-avatar--online' : ''}`
          + (unread ? ' collab-avatar--unread' : '')}
      style={style}
      onClick={onClick}
      title={title}
      aria-label={unread
        ? `Chat with ${person.display_name}, ${unread} unread`
        : `Chat with ${person.display_name}`}
    >
      {person.initials}
      {!!unread && <span className="collab-unread-dot" aria-hidden="true" />}
    </button>
  );
}

function PresenceRail({ people = [], onOpen }) {
  const [showAll, setShowAll] = useState(false);
  const [directory, setDirectory] = useState(null);
  const wrapRef = useRef(null);

  // Read the directory the first time the panel is opened, and only then.
  useEffect(() => {
    if (!showAll || directory !== null) return undefined;
    let alive = true;
    collabApi.directory()
      .then((data) => { if (alive) setDirectory(data.people || []); })
      .catch(() => { if (alive) setDirectory([]); });
    return () => { alive = false; };
  }, [showAll, directory]);

  useEffect(() => {
    if (!showAll) return undefined;
    const away = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowAll(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [showAll]);

  // Unread first, then the people who are actually here, then by name. A dot nobody
  // can see because its owner sorted twelfth is a dot that did not work.
  const sorted = [...people].sort((a, b) => {
    if (!!b.unread !== !!a.unread) return (b.unread || 0) - (a.unread || 0);
    if (b.online !== a.online) return b.online ? 1 : -1;
    return String(a.display_name).localeCompare(String(b.display_name));
  });

  // A circle means "here now" (or "has written to you"). Somebody offline whom you merely
  // talked to once is still sent by the server so the thread stays reachable — but in the
  // overflow list, never as a face in the header, or it reads as a user who never logs out.
  const onRail = sorted.filter((p) => p.online || p.unread);
  const head = onRail.slice(0, MAX_VISIBLE);
  const rest = sorted.filter((p) => !head.includes(p));
  const restOnline = rest.filter((p) => p.online || p.unread).length;

  if (!people.length) return null;

  const known = new Set(sorted.map((p) => String(p.user_id)));
  const others = (directory || []).filter((p) => !known.has(String(p.user_id)));

  return (
    <div className="collab-rail" ref={wrapRef}>
      <div className="collab-rail-stack">
        {head.map((p) => (
          <Avatar key={p.user_id} person={p} stacked onClick={() => onOpen(p.user_id)} />
        ))}
        {rest.length > 0 && (
          <button
            type="button"
            className="collab-avatar collab-avatar--more"
            onClick={() => setShowAll((v) => !v)}
            aria-label={restOnline ? `Show ${restOnline} more` : 'Recent conversations'}
            title={restOnline ? undefined : 'Recent conversations'}
            aria-expanded={showAll}
          >
            {restOnline ? `+${restOnline}` : '…'}
            {rest.some((p) => p.unread) && <span className="collab-unread-dot" aria-hidden="true" />}
          </button>
        )}
      </div>

      <span className="collab-rail-sep" aria-hidden="true" />

      {showAll && (
        <div className="collab-rail-overflow">
          {rest.map((p) => (
            <button
              key={p.user_id}
              type="button"
              className="collab-rail-row"
              onClick={() => { setShowAll(false); onOpen(p.user_id); }}
            >
              <Avatar person={p} size={24} bare onClick={() => { setShowAll(false); onOpen(p.user_id); }} />
              <span className="collab-rail-row-text">
                <span className="collab-rail-row-name">{p.display_name}</span>
                <span className="collab-rail-row-where">
                  {p.online ? (p.where || 'Online') : 'Offline'}
                </span>
              </span>
              {!!p.unread && <span className="collab-rail-row-count">{p.unread}</span>}
            </button>
          ))}

          {/* Everybody else on the account. They are not here, which is not the same as
              unreachable — the message waits for them. */}
          {others.length > 0 && (
            <div className="collab-rail-overflow-head">Everyone else</div>
          )}
          {directory === null && (
            <div className="collab-row-menu-empty">Loading people…</div>
          )}
          {others.map((p) => (
            <button
              key={p.user_id}
              type="button"
              className="collab-rail-row"
              onClick={() => { setShowAll(false); onOpen(p.user_id); }}
            >
              <Avatar person={{ ...p, online: false }} size={24} bare
                      onClick={() => { setShowAll(false); onOpen(p.user_id); }} />
              <span className="collab-rail-row-text">
                <span className="collab-rail-row-name">{p.display_name}</span>
                <span className="collab-rail-row-where">Offline</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PresenceRail;
